import Page from '../../components/shared/Page/Page';
import SecondaryFooter from '../../components/shared/SecondaryFooter/SecondaryFooter';
import StyledContentLayout from '../../styles/StyledContentLayout';
import useAuth from '../../hooks/useAuth';

const TermsOfService: React.FC = () => {
  const { currentUser } = useAuth();

  return (
    <Page title="Elora | Terms of Service">
      <StyledContentLayout>
        <h1>Terms and Conditions of Use</h1>

        <h2>
          <strong>Definitions</strong>
        </h2>

        <p>
          <strong>ACCOUNT</strong> means the User&#39;s personal account created
          to access the Services, protected by a unique username and password.
        </p>

        <p>
          <strong>ADMINISTRATOR USER:</strong> refers to the individual
          designated by the company to manage access to the ELORA SERVICES.
        </p>

        <p>
          <strong>AGREEMENT</strong> means this End User Agreement (the Terms
          and Conditions of Use governing ELORA&#39;s Services), including any
          additional terms or policies expressly incorporated by reference. It
          defines the rights and obligations of the User and ELORA regarding use
          of the Services.
        </p>

        <p>
          <strong>CONFIDENTIAL INFORMATION</strong> means any information that
          is either marked as confidential or that a reasonable person would
          understand as confidential due to its nature or the context in which
          it is shared.
        </p>

        <p>
          <strong>ELORA</strong> means the provider of the Services, i.e. ELORA,
          the company offering the ELORA Services.
        </p>

        <p>
          <strong>LICENCE:</strong> refers to the right to use the Services
          allowing the User to access it and use it for the period for which it
          is granted.
        </p>

        <p>
          <strong>SERVICES</strong> means all Services provided by ELORA to the
          User, including the access to the website https://www.elora.lu/.
        </p>

        <p>
          <strong>TERMS AND CONDITIONS</strong> means all of these Terms and
          Conditions of Use and Rules of Use and any additional Terms and
          Conditions agreed to which are expressly mentioned in these Terms and
          Conditions of Use or to which reference is made in these Terms and
          Conditions of Use.
        </p>

        <p>
          <strong>USER</strong> means any natural person who is authorized by
          the Administrator User to access and use the Services, excluding the
          Administrator User themselves.
        </p>

        <h2>
          <strong>Object</strong>
        </h2>

        <p>
          Once access to the Services has been confirmed, the User can access
          ELORA Services and is granted a non-exclusive, worldwide and revocable
          right to use the Platform provided that the terms and conditions
          included in the present Agreement are met.
        </p>

        <p>
          ELORA provides access to an artificial intelligence platform designed
          to support professionals in handling calls and chats with their
          clients. The following Services are accessible by the User on the date
          of acceptance of the Agreement:
        </p>

        <ul>
          <li>
            <strong>The Call Agent,</strong> is an AI assistant that answers
            incoming calls in multiple languages, replies to customer questions,
            transfer calls, when needed, books appointments, and performs other
            tasks predefined by the company.
          </li>
          <li>
            <strong>The Chat Agent,</strong> which enables Users to ask
            practical, technical, regulatory or legal questions in natural
            language and receive AI-generated answers, enriched by ELORA&#39;s
            proprietary legal knowledge base. The Chat Agent can also be
            embedded on the company&#39;s website and made accessible to the
            public.
          </li>
        </ul>

        <p>
          Both tools can be customized with company data and guidelines to
          improve relevance. The overall aim is to enhance communication,
          productivity, and client service for professionals using the platform.
        </p>

        <p>
          This End User Agreement governs all access to and use of the ELORA
          Services (including the website https://www.elora.lu and any tools or
          content made available through it) and defines the respective rights
          and responsibilities of both the Administrator User, the User and
          ELORA.
        </p>

        <p>
          By accessing or using the ELORA Services, the User acknowledges having
          read, understood, and accepted this Agreement, and agrees to comply
          with all provisions contained herein.
        </p>

        <h2>
          <strong>Access to the Services</strong>
        </h2>

        <p>
          Access to the ELORA Services is provided online via a secure web
          platform, the address of which is communicated by ELORA to the client.
          Each User must have a compatible browser and internet connection to
          access the platform.
        </p>

        <p>
          The Administrator User must be designated to create the company&#39;s
          workspace and manage access for other authorized Users. During
          registration, the Administrator User must fill in a form with the
          required information, including the name of the company, the chosen
          subdomain (if applicable), and their contact details (name, email
          address, and phone number). Responsibility for the accuracy of the
          data lies with the Administrator User, and ELORA shall not be held
          responsible for any errors or omissions.
        </p>

        <p>
          Each User accesses the Services via a personal account protected by a
          username and password. These login credentials are strictly personal
          and confidential, and the User must not share them with any third
          party. The User is solely responsible for maintaining the
          confidentiality and security of their login information.
        </p>

        <p>
          The User assumes full responsibility for all actions performed using
          their login credentials, whether or not such actions were authorized.
        </p>

        <p>
          ELORA shall not be held liable for any damage, loss of data or
          unauthorized access resulting from negligence in managing login
          credentials.
        </p>

        <p>
          By using the Services, each User confirms that they have been
          authorized by their organization to access the platform for
          professional use. If the User is using ELORA Services in the context
          of an organization or employer, the User guarantees that they have
          that organization&#39;s permission to use the Services for
          professional purposes. ELORA may, at its discretion, require
          verification of a User&#39;s authorization or identity as part of the
          registration or authentication process.
        </p>

        <h2>
          <strong>Services Levels and Support</strong>
        </h2>

        <p>
          ELORA is committed to providing a reliable and professional Services
          to its business Users. This outlines the applicable Services level
          commitments and support conditions.
        </p>

        <h3>
          <strong>Availability Guarantee</strong>
        </h3>

        <ul>
          <li>
            ELORA will use commercially reasonable efforts to ensure that the
            Services are available 99% of the time on a monthly basis.
          </li>
          <li>
            Any unavailability resulting from force majeure events or
            third-party cloud providers is excluded from the availability
            calculation.
          </li>
        </ul>

        <h3>
          <strong>Customer Support – Maintenance</strong>
        </h3>

        <p>
          Customer support is available Monday to Friday, from 9:00 a.m. to 6:00
          p.m. CET, via email, integrated chat, or client portal.
        </p>

        <p>
          ELORA will use best efforts to meet the following target response
          times for support requests:
        </p>

        <ul>
          <li>Critical issue (Services unavailable): within 2 hours;</li>
          <li>Major issue (key functionality degraded): within 4 hours;</li>
          <li>Minor issue (non-blocking problem): within 1 business day.</li>
        </ul>

        <p>
          Planned maintenance operations are announced at least 48 hours in
          advance and are scheduled outside of working hours (10:00 p.m. – 6:00
          a.m. CET).
        </p>

        <h3>
          <strong>Cloud providers</strong>
        </h3>

        <p>
          ELORA relies on third-party cloud infrastructure providers (such as
          AWS, Azure, or GCP) to deliver its Services. Any Services interruption
          caused by a failure on the part of these providers is excluded from
          the Services availability calculations and shall not give rise to any
          compensation.
        </p>

        <h3>
          <strong>Data Backup</strong>
        </h3>

        <ul>
          <li>
            ELORA performs daily backups of all data, with a retention period of
            365 days.
          </li>
          <li>
            All data is encrypted in transit (TLS 1.2 or higher) and at rest
            (AES-256).
          </li>
          <li>
            ELORA ensures compliance with the GDPR and follows industry-standard
            data security practices (see Section 7 for further details).
          </li>
        </ul>

        <h2>
          <strong>User Responsibilities</strong>
        </h2>

        <p>
          Each User must use the ELORA Services properly and follow this
          Agreement. The Services must only be used for lawful, professional
          purposes. Users must not misuse or interfere with the Services and
          must not try to access restricted parts of the platform.
        </p>

        <p>
          Users must keep their login details secure and not share their
          account. They are responsible for the content they submit and must
          ensure they have the right to use it (especially if it includes
          personal data or third-party materials).
        </p>

        <p>
          Any misuse may lead to suspension or termination of access to the
          Services. ELORA may also remove any content that violates this
          Agreement or the law.
        </p>

        <h2>
          <strong>Administrator User Responsibilities</strong>
        </h2>

        <p>
          The Administrator User is the main contact responsible for managing
          access and use of the ELORA Services within their company. They must
          provide accurate information during registration and update it if
          anything changes. The Administrator is also in charge of managing
          access for other Users: creating, modifying or removing access when
          necessary.
        </p>

        <p>
          Each Administrator User, on behalf of the company or entity it
          represents, is expected to ensure that all Users comply with this
          Agreement and use the Services only for authorized and professional
          purposes. ELORA may send important notices (such as service updates or
          changes) to the Administrator User(s), who must then share them with
          relevant Users.
        </p>

        <p>
          The Administrator User is also responsible for all payment and billing
          matters related to the use of the Services. In addition, they act as
          the main point of contact for any questions or technical issues.
        </p>

        <h2>
          <strong>Intellectual Property Rights and Licence</strong>
        </h2>

        <h3>
          <strong>Rights to use the Services</strong>
        </h3>

        <p>
          ELORA grants the User a limited, non-exclusive, non-transferable right
          to access and use the copyrights and other intellectual property
          rights attached to the software operating the Services for the User,
          and all rights to use attached thereto.
        </p>

        <p>The User shall not, directly or indirectly:</p>

        <ul>
          <li>
            copy, reproduce, or distribute any part of the Services or their
            content;
          </li>
          <li>
            modify, translate, adapt, or create derivative works from the
            software operating the Services or any software, models or content
            provided;
          </li>
          <li>
            disassemble, decompile, or attempt to reverse-engineer any component
            of the software operating the Services;
          </li>
          <li>
            remove or alter any copyright, trademark, or proprietary notices;
          </li>
          <li>use the Services to build a competing product or service;</li>
          <li>share access credentials with unauthorized third parties.</li>
        </ul>

        <p>
          This Licence is granted for the duration of the Agreement and is
          conditioned on the User&#39;s compliance with this Agreement.
        </p>

        <p>
          The software operating the Services is licenced, not sold, to the
          User. The User acquires no ownership rights, title, or interest in the
          software operating the Services or components thereof (including any
          copyright, related rights, or other intellectual property rights),
          which remain the exclusive property of ELORA.
        </p>

        <p>
          ELORA reserves the right to monitor and audit the User&#39;s use of
          the software operating the Services and of the Services at any time,
          without prior notice, to ensure compliance with the terms of this
          Agreement.
        </p>

        <p>
          All rights not expressly granted to the User under this Agreement are
          reserved by ELORA.
        </p>

        <h3>
          <strong>Intellectual Property Ownership</strong>
        </h3>

        <p>
          All intellectual property rights in and to the software operating the
          Services, including but not limited to the website, software,
          algorithms, AI models, the proprietary legal knowledge base,
          documentation, and ELORA&#39;s trademarks and logos, are and shall
          remain the exclusive property of ELORA.
        </p>

        <p>
          Nothing in this Agreement transfers any ownership of such intellectual
          property to the User. The User is only granted the limited rights of
          use as described above. The User shall not remove or alter any
          copyright, trademark, or proprietary notices on the Services or any
          content obtained from it.
        </p>

        <p>
          Any content or data that the User provides to the software operating
          the Services remains the User&#39;s property.
        </p>

        <p>
          By submitting or uploading any content to the software operating the
          Services, the User agrees that ELORA may use such content to improve
          the Services&#39; functionality, enhance the User experience, conduct
          analytics, and, where relevant, contact Users regarding products or
          Services mentioned during their use of the Services.
        </p>

        <p>
          Any content generated during the performance of the Services based on
          inputs belongs to the User. ELORA does not claim ownership over this
          output. It may be used for business purposes, but the User remains
          responsible for such use. ELORA does not guarantee the accuracy of the
          content and is not liable for any issues resulting from its use.
        </p>

        <h2>
          <strong>Artificial Intelligence</strong>
        </h2>

        <h3>
          <strong>User&#39;s Responsibility for Input Information</strong>
        </h3>

        <p>
          The User is exclusively and fully responsible for all content and data
          they enter into the software operating the Services, including through
          the Call Agent or Chat Agent. They must make sure that any information
          they provide is accurate, lawful, and that they have the necessary
          rights to use and share it. The User must not submit content that
          infringes on third-party rights (such as copyright or privacy), or
          that is illegal, offensive, or harmful.
        </p>

        <p>
          When picking up a call or calling a user, the Agent will inform all
          call participants that they are interacting with an AI system. While
          ELORA does not actively monitor what Users submit, it may remove or
          block content that violates this Agreement or the law. The User is
          responsible for any issues caused by the content they provide and
          agrees to cover any losses or legal claims resulting from misuse.
        </p>

        <h3>
          <strong>AI-Generated Content and Limitations</strong>
        </h3>

        <p>
          The User understands and acknowledges that the Service&#39;s responses
          and outputs are generated by an artificial intelligence system. While
          ELORA strives to provide useful and accurate information, the
          AI-generated answers, summaries, and content are provided for general
          informational purposes only and do not constitute professional advice.
        </p>

        <ul>
          <li>
            ELORA makes no guarantee that any AI-generated content is correct,
            complete, or up to date.
          </li>
          <li>
            ELORA does not guarantee that AI-generated content is accurate or
            fully adapted to the User&#39;s situation. It is provided as a
            support tool and should be used with caution.
          </li>
        </ul>

        <p>
          ELORA may use anonymized or aggregated user data (including User&#39;s
          inputs) to improve its AI models and the Service&#39;s functionality.
          However, ELORA will never share User&#39;s personal data or
          confidential information with any third party except as necessary to
          provide the Service (and in accordance with this Agreement and the
          law).
        </p>

        <h2>
          <strong>Data protection and Privacy</strong>
        </h2>

        <p>
          Please review our Privacy Notice accessible via our website, which
          also governs the User&#39;s use of ELORA Services, to understand our
          practices.
        </p>

        <p>
          Users may request of their personal data at any time. Requests can be
          sent by email to: <strong>GDPR@elora.lu</strong>.
        </p>

        <h2>
          <strong>Confidentiality</strong>
        </h2>

        <p>
          Each party agrees to keep confidential all non-public information
          shared under this Agreement.
        </p>

        <p>
          The User agrees not to share ELORA&#39;s Confidential Information
          including, but not limited, its technology, algorithms, legal
          database, commercial terms, and any non-public business or technical
          information except as necessary to use the Service in accordance with
          this Agreement. This information may only be used to access and use
          the Services and must be protected with reasonable care.
        </p>

        <p>
          Similarly, ELORA will treat as confidential any non-public information
          provided or generated through the Service. For example, the content of
          phone calls processed by the Call Agent, questions asked via the Chat
          Agent, and any internal documents or data uploaded will be considered
          Confidential Information of the User. ELORA will not use or disclose
          the User&#39;s confidential information outside the scope of providing
          the Service, except to ELORA&#39;s own service providers who are bound
          by strict confidentiality obligations, or as required by law.
        </p>

        <p>
          These confidentiality duties do not apply to information that was
          already known without restriction, becomes public without fault or is
          legally obtained from someone else. Disclosure may also be required by
          law, in which case the disclosing party should be notified in advance
          when possible.
        </p>

        <p>
          The duty to protect Confidential Information continues throughout the
          term of the Agreement and for one year after it ends, or longer if
          required by law or if the information qualifies as a trade secret.
        </p>

        <h2>
          <strong>Penalties</strong>
        </h2>

        <p>
          If the User breaches this Agreement or uses the Services in an
          unlawful or unauthorized way, ELORA may suspend or temporarily disable
          the User&#39;s access to the Service immediately, without prior
          notice. In more serious cases (or repeated breaches), ELORA may also
          terminate the User&#39;s Account and this Agreement, with immediate
          effect, without refund and without being held liable. ELORA will make
          reasonable efforts to notify the User (or their organization) of such
          suspension or termination, unless legal or security concerns require
          immediate action.
        </p>

        <p>
          The User agrees to compensate ELORA for any loss, damage, or legal
          cost resulting from a breach of this Agreement, misuse of the
          Services, or any content or data provided by the User that causes harm
          or infringes third-party rights. This obligation survives termination.
          ELORA will notify the User of any third-party claim and may
          participate in the defence. The User may not settle any such claim
          without ELORA&#39;s prior written consent.
        </p>

        <p>
          In addition to these remedies, ELORA may take any other legal action
          available to prevent further harm. The fact that ELORA does not act
          immediately does not mean it waives its right to do so later.
        </p>

        <p>
          As part of its Services commitments, ELORA offers compensation in case
          of prolonged unavailability of the Services. If monthly uptime falls
          below 99%, the User will receive a Services credit equal to 10% of
          their monthly quota. If uptime drops below 90%, the credit will
          increase to 20%. These credits are the User&#39;s exclusive remedy in
          such cases. However, errors or inaccuracies in AI-generated content do
          not qualify for Services credits or compensation.
        </p>

        <h2>
          <strong>Warranties and Disclaimers</strong>
        </h2>

        <p>
          ELORA confirms that it has the legal right to offer the Services and
          will provide it with reasonable care and attention. ELORA will make
          its best efforts to ensure the Services are reliable and available, as
          described in Section 4.
        </p>

        <p>
          However, except for what is clearly stated in this Agreement, the
          Services are provided &#34;as is&#34; and &#34;as available&#34;.
          ELORA does not guarantee that the Services will always meet the
          User&#39;s needs, or that results (such as answers or summaries) will
          always be accurate or complete. Because the ELORA Services uses
          artificial intelligence and may rely on external data, some outputs
          may be incorrect, incomplete, or unsuitable for certain situations.
        </p>

        <p>
          In some cases, ELORA may allow software elements, parts of the
          Service, such as the Chat Agent, to be installed directly on the
          User&#39;s own systems. While ELORA tests all features and follows
          industry best practices, ELORA cannot be responsible for any security
          issues, performance problems, or errors caused by this type of
          installation. The User&#39;s organization is fully responsible for its
          own systems and interaction with ELORA Service.
        </p>

        <p>
          The User is solely responsible for how they use the Services and for
          reviewing any content or drafts before relying on them or sharing
          them. ELORA does not guarantee that the Services will help the User
          comply with any law or regulation. The Services are a support tool
          only, and final decisions remain the responsibility of the User.
        </p>

        <p>
          In some cases, the Services may include or depend on third-party
          software, data, or content (such as cloud services or legal
          databases). ELORA is not responsible for issues caused by these
          external components and does not guarantee their quality or
          availability.
        </p>

        <p>
          ELORA cannot be held responsible for the improper performance of ELORA
          Services due to incorrect or out-of-date information entered by the
          User.
        </p>

        <p>
          ELORA does not warrant that the ELORA Services will operate error-free
          or that the Services or its servers are free of computer viruses or
          other harmful components. ELORA shall not be liable for any costs
          incurred because of lost profits or lost data, as a result of the use
          of ELORA sites or the content contained therein.
        </p>

        <p>
          ELORA cannot be held responsible for Services breakdowns or damage
          caused by intrusion, hacking of the system by a third party or illicit
          extraction of data. However, ELORA undertakes to use its best efforts
          to implement security methods in line with the current state of
          technical knowledge.
        </p>

        <p>
          ELORA will use reasonable efforts to protect any information or data
          User transmits to ELORA in connection with any ELORA Services or
          feature. However, the User agrees that sending such information or
          data is done at the User&#39;s own risk, and releases ELORA from any
          and all liability for any loss or liability related to such
          information in any manner whatsoever.
        </p>

        <p>
          Nothing in this Agreement creates any warranty beyond what is
          expressly written here. If the law in the User&#39;s country does not
          allow certain warranty exclusions, this section will apply only as far
          as the law permits.
        </p>

        <h2>
          <strong>Limitation of Liability</strong>
        </h2>

        <p>
          To the fullest extent allowed by law, ELORA is not responsible for any
          indirect or unexpected damage caused using the Services, such as lost
          profits, lost data, business interruption, or reputational harm, even
          if ELORA was warned about the possibility of such damage.
        </p>

        <p>
          If ELORA is found liable for any reason, its total responsibility will
          be limited to the amount the User (or their company) actually paid to
          use the Services in the last 12 months. If the User has not paid any
          fees (for example, during a free trial), ELORA&#39;s maximum liability
          will be EUR 100.
        </p>

        <p>
          Any claim related to the Services must be made within one year after
          the issue occurred, or it will be considered waived.
        </p>

        <p>
          Some countries, states, provinces, or other jurisdictions do not allow
          the exclusion of certain warranties, or the limitation of liability as
          stated in this Section, so the terms herein may not fully apply to the
          User.
        </p>

        <h2>
          <strong>Term and Termination</strong>
        </h2>

        <h3>
          <strong>Term</strong>
        </h3>

        <p>
          This Agreement commences on the earlier of the date the User first
          opened an Account to use the Services or the date when the User
          accepted these terms. These terms will remain in full force and effect
          while using the Services, unless terminated earlier.
        </p>

        <h3>
          <strong>Termination by ELORA</strong>
        </h3>

        <p>
          ELORA may suspend or terminate access to the Service (and accordingly
          terminate this Agreement), at any time and without prior notice, if
          the User seriously breaches this Agreement or repeatedly fails to
          comply with its terms. Additionally, ELORA reserves the right to
          terminate this Agreement (and/or access to the Service) if ELORA is
          required to do so by law or a court order, or if ELORA decides to
          discontinue the Service.
        </p>

        <p>In such cases, ELORA will give prior notice when possible.</p>

        <p>
          ELORA is not responsible for any loss or damages resulting from such
          termination.
        </p>

        <h3>
          <strong>Termination by user</strong>
        </h3>

        <p>
          The User may terminate this Agreement at any time by notifying ELORA
          of the decision to stop using the Service and by closing the account.
        </p>

        <p>
          Upon such a request, ELORA will deactivate the Account. Except for
          provisions which are intended to survive termination, all rights and
          obligations under this Agreement will cease once the Agreement is
          terminated at the User&#39;s request.
        </p>

        <h3>
          <strong>Effect of termination</strong>
        </h3>

        <p>
          When this Agreement ends for any reason, the User&#39;s right to
          access and use the Services will immediately cease, and ELORA may
          deactivate the User&#39;s Account and login credentials. The User may
          no longer be able to access any information, files, or content stored
          within their Account, and such data may be deleted. ELORA shall not be
          liable for any loss resulting from the suspension or termination of
          access. Personal data will be handled in accordance with Section 7 and
          applicable data protection laws. In general, ELORA will retain
          personal data for a limited time (up to one year), after which it will
          be securely deleted or anonymised.
        </p>

        <h2>
          <strong>Governing Law and Dispute Resolution</strong>
        </h2>

        <p>
          This Agreement shall be governed by and interpreted in accordance with
          the laws of the Grand Duchy of Luxembourg, without regard to its
          conflict of law principles.
        </p>

        <p>
          Any dispute, claim, or controversy arising out of or relating to this
          Agreement or the use of ELORA&#39;s Services by the User shall be
          subject to the exclusive jurisdiction of the courts of the district of
          Luxembourg City, Luxembourg.
        </p>

        <h2>
          <strong>General Provisions</strong>
        </h2>

        <p>
          If any provision of this Agreement is held to be invalid, illegal,
          void or unenforceable by reason of any applicable law, such provision
          shall be severed and the remaining provisions of this Agreement shall
          remain in full force and effect and the rights and obligations of the
          parties shall be construed and enforced accordingly.
        </p>

        <p>
          ELORA reserves the right to make changes to this Agreement at any
          time, for example to reflect updates to the Services, legal
          requirements, or internal practices. Users will be informed of any
          material changes. Continued use of the Services after such changes
          constitutes acceptance of the revised terms.
        </p>

        <p>
          ELORA reserves the right to improve, modify, or discontinue all or
          part of the Services at any time, provided that such changes do not
          materially reduce the overall functionality. ELORA may also revise its
          pricing structure. In such cases, Administrator Users will be notified
          in advance by appropriate means. Continued use of the Services after
          the effective date of the changes shall constitute acceptance of the
          revised terms. If the Administrator User does not agree, it may
          terminate the Agreement under Section 12.
        </p>
      </StyledContentLayout>
      {!currentUser && <SecondaryFooter />}
    </Page>
  );
};

export default TermsOfService;
